import React from 'react';
import PropTypes from 'prop-types';
import {matchPath, NavLink as ReactRouterNavLink} from 'react-router-dom';

import {Link, ListItem, Menu} from '@spothero/ui';
import IconChevronDown from '@spothero/icons/chevron-down';

import {useFacilityMultiSelect} from 'common/facility-sidebar/context/FacilityMultisSelectContext';
import MotionIcon from 'common/animation/MotionIcon';
import MenuButton from '../common/MenuButton';
import NavigationDropDownMenu from './NavigationDropDownMenu';

const navigationItemStyleProps = {
    color: 'black',
    borderColor: 'white',
    backgroundColor: 'white',
    paddingX: 4,
    paddingY: 2,
    fontWeight: 'semibold',
    borderRadius: 'base',
    fontSize: 'sm',
    _hover: {
        borderColor: 'gray.light',
        backgroundColor: 'gray.light',
        color: 'black',
    },
    _active: {
        borderColor: 'gray.light',
        backgroundColor: 'gray.light',
        color: 'black',
    },
    _focus: {
        boxShadow: 'outline',
    },
    sx: {
        '&.active': {
            borderColor: 'navBlue',
            backgroundColor: 'navBlue',
            color: 'primary.600',
            _hover: {
                color: 'primary.700',
            },
            _active: {
                color: 'primary.600',
                _hover: {
                    color: 'primary.700',
                },
            },
        },
    },
};

const getNavigationItemTabIconStyleProps = (isOpen) => ({
    boxSize: 4,
    marginLeft: 2,
    animate: {
        rotate: isOpen ? -180 : 0,
    },
    transition: {
        type: 'spring',
        damping: 25,
        stiffness: 180,
        duration: 0.125,
        delay: 0,
    },
});

const NavigationItem = ({children, label, paths, route}) => {
    const isMultiSelectEnabled = useFacilityMultiSelect() || false;

    // TODO: should we be explicitly checking for control panel preview here?
    const isActivePath = matchPath(
        window.location.pathname.replace(
            /\/(control\-panel(?:\-preview)?)/,
            ''
        ),
        {path: paths, exact: true}
    )?.isExact;

    // This selector is used for targeting specific Navigation Menu Items within Intercom's walkthroughs - DO NOT REMOVE
    const intercomNavItemSelector = `${label
        .replaceAll(' ', '')
        .replace('&', '')}MenuButton`;

    return (
        <ListItem marginBottom={0}>
            {children ? (
                <Menu variant="full" placement="bottom" closeOnSelect isLazy>
                    {({isOpen}) => (
                        <>
                            <MenuButton
                                data-testid={intercomNavItemSelector}
                                {...navigationItemStyleProps}
                                {...(isActivePath && {className: 'active'})}
                            >
                                {label}{' '}
                                <MotionIcon
                                    name="chevron-down"
                                    as={IconChevronDown}
                                    {...getNavigationItemTabIconStyleProps(
                                        isOpen
                                    )}
                                />
                            </MenuButton>
                            <NavigationDropDownMenu
                                isMultiSelect={isMultiSelectEnabled}
                                menuContentArray={children}
                            />
                        </>
                    )}
                </Menu>
            ) : (
                <Link
                    key={route}
                    data-testid={intercomNavItemSelector}
                    {...(route && {as: ReactRouterNavLink, to: route})}
                    isActive={(match, location) => {
                        // match the current path to the associated paths for a given route
                        const routeMatch = matchPath(location.pathname, {
                            path: paths,
                        });

                        return routeMatch?.isExact;
                    }}
                    {...navigationItemStyleProps}
                >
                    {label}
                </Link>
            )}
        </ListItem>
    );
};

NavigationItem.defaultProps = {
    routes: [],
};

NavigationItem.propTypes = {
    children: PropTypes.arrayOf(
        PropTypes.shape({
            heading: PropTypes.string.isRequired,
            submenu: PropTypes.arrayOf(
                PropTypes.shape({
                    label: PropTypes.string.isRequired,
                    subtitle: PropTypes.string,
                    route: PropTypes.string.isRequired,
                    isDisabled: PropTypes.bool,
                    isNew: PropTypes.bool,
                })
            ),
        })
    ),
    isMultiSelect: PropTypes.bool,
    label: PropTypes.string.isRequired,
    paths: PropTypes.arrayOf(PropTypes.string).isRequired,
    route: PropTypes.string,
    routes: PropTypes.arrayOf(PropTypes.string),
};

export default NavigationItem;
